import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faClock,
  faHourglassHalf,
  faLanguage,
  faSchool,
  faUserGroup,
} from '@fortawesome/free-solid-svg-icons';
import { FormatSettings } from '@progress/kendo-angular-dateinputs';
import { NotificationService } from '@progress/kendo-angular-notification';
import { some } from 'lodash';
import moment from 'moment';
import { Mandate } from 'src/app/modules/dashboard/interface/dashboard-response';
import { IEPService } from 'src/app/modules/iep/iep-document/services/iep.services';
import { AuthStore } from 'src/app/stores/auth.store';
import { kendoDateFormat } from '../../constants/dates';
import {
  dialogAnimationDuration,
  dialogTimer,
} from '../../constants/dialog-specs';
import { NOTIFICATION_MESSAGES } from '../../constants/notification-messages';
import { Permissions } from '../../enums/permissions';
import {
  CapatureFirstAttendResponse,
  PlacementAttendanceResponse,
} from '../../interfaces/capture-first-attend';
import { PlacementFormValue } from '../../interfaces/capture-first-attend-form-value';
import { FirstAttendDateRequest } from '../../interfaces/first-attend-date';
import { ISedmsFormInput } from '../../interfaces/form/sedms-form-input.model';
import { Permission } from '../../models/permission';
import { User } from '../../models/user';
import { ActivatedRoute } from '@angular/router';


@Component({
  selector: 'app-capture-first-attend-date-dialog',
  templateUrl: './capture-first-attend-date-dialog.component.html',
  standalone: false,
})
export class CaptureFirstAttendDateDialogComponent implements OnInit {
  form: FormGroup;
  @Input() isTwelveMonth?: boolean;
  @Input() nycId: string;
  @Input() studentIEP: {
    programServices?: { programs?: Mandate[] };
    programs?: Mandate[];
    seitAgency?: unknown;
  }; // TODO: This will need to be change to an appropriate type. Too many different types are being used across the app for this object.
  loadSpinner: boolean;
  @Output() closeDialog: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() saved: EventEmitter<void> = new EventEmitter<void>();
  programsList: Mandate[];
  placementsList: PlacementAttendanceResponse[];
  todayDate: Date;
  isPALAvailable: boolean;
  public format: FormatSettings = kendoDateFormat;
  isShowPlacementDetails: boolean = false;
  isShowIepProgramDetails: boolean = false;
  readonly seitString: string = 'SEIT';
  readonly firstIndex: number = 0;
  readonly iepProgramsMinCount: number = 2;
  readonly specialClass: number = 3;
  readonly scis: number = 8;
  user: User;
  nycid: string;

  constructor(
    library: FaIconLibrary,
    private fb: FormBuilder,
    public route: ActivatedRoute,
    private service: IEPService<ISedmsFormInput>,
    private notificationService: NotificationService,
    private cdref: ChangeDetectorRef,
    private authStore: AuthStore
  ) {
    library.addIcons(
      faLanguage,
      faClock,
      faHourglassHalf,
      faUserGroup,
      faSchool
    );
    this.form = new FormGroup({
      placementFirstAttendDateForm: new FormArray([]),
      iepProgramsFirstAttendDateForm: new FormArray([]),
    });
    this.loadSpinner = true;
    this.todayDate = moment().startOf('day').toDate();
    this.authStore.UserInfo$.subscribe((userInfo: User) => {
      this.user = userInfo;
    });
    this.route.paramMap.subscribe((params) => {
      this.nycid = params.get('id');

    });
  }

  ngOnInit(): void {
    this.service
      .getCaptureFirstDateByNycId(this.nycId ? this.nycId : this.nycid)
      .subscribe((data: CapatureFirstAttendResponse) => {
        this.programsList = data.iepProgrms;
        const placements: PlacementAttendanceResponse[] = data.placementAttendenceResponces;
        this.placementsList =
          placements && this.isTwelveMonth !== null
            ? placements.filter(
                (p: PlacementAttendanceResponse) => p.summerFlag === this.isTwelveMonth
              )
            : placements;
        this.isShowPlacementDetails = this.placementsList !== null;

        this.isPALAvailable = data.isPALAvailable;
        if (this.programsList) {
          this.isShowIepProgramDetails = true;
          if (
            this.programsList.some(
              (item: Mandate) =>
                item.classroomsetting !== this.specialClass &&
                item.classroomsetting !== this.scis
            )
          ) {
            this.programsList = this.programsList.filter(
              (item: Mandate) =>
                item.classroomsetting !== this.specialClass &&
                item.classroomsetting !== this.scis
            );
          }
          this.addIepProgramsFirstAttendDateForm();
          this.loadSpinner = false;
        }
        if (this.placementsList !== null) {
          this.isShowPlacementDetails = true;
          this.addPlacementFirstAttendDateForm();
          this.loadSpinner = false;
        }
      });

    this.form.get('placementFirstAttendDateForm').valueChanges.subscribe(() => {
      this.form.updateValueAndValidity();
      this.cdref.detectChanges();
    });
  }

  onCloseDialog(): void {
    this.closeDialog.emit(false);
  }

  checkIfItHasOnlyTenAndTwelveSEIT(): boolean {
    return (
      this.studentIEP?.programServices?.programs?.length ===
      this.iepProgramsMinCount &&
      this.studentIEP?.programServices?.programs.filter(
        (mandate: Mandate) => mandate.mandateType === this.seitString
      )?.length === this.iepProgramsMinCount
    );
  }

  get validation(): boolean {
    return !this.form?.valid;
  }

  createPlacementAttendenceResponseForm(
    data: PlacementAttendanceResponse
  ): FormGroup {
    return this.fb.group({
      placementSk: [data.placementSk],
      iepFormSk: [data.iepFormSk],
      summerFlag: [data.summerFlag],
      iepType: [data.iepType],
      iepPlacementRecommendation: [data.iepPlacementRecommendation],
      schoolDBN: [data.schoolDBN],
      schoolName: [data.schoolName],
      schoolYear: [data.schoolYear],
      class: [data.class],
      district: [data.district],
      authorizationDate: [
        data.authorizationDate
          ? moment(data.authorizationDate).startOf('day').toDate()
          : null, [Validators.required]
      ],
      grade: [data.grade],
      firstAttendDate: [
        data.firstAttendDate
          ? moment(data.firstAttendDate).startOf('day').toDate()
          : null, [Validators.required]
      ],
      mandateSk: [data.mandateSk],
    });
  }

  createIEPProgramForm(data: Mandate): FormGroup {
    return this.fb.group({
      mandateSk: [data.mandateSk],
      mandateType: [data.mandateType],
      mandateDescription: [data.mandateDescription],
      frequency: [data.frequency],
      language: [data.language],
      groupType: [data.groupType],
      isTwelveMonth: [data.isTwelveMonth],
      duration: [data.duration],
      firstAttendDate: [
        data.firstAttendDate
          ? moment(data.firstAttendDate).startOf('day').toDate()
          : null,
      ],
      authorizationDate: [
        data.authorizationDate
          ? moment(data.authorizationDate).startOf('day').toDate()
          : null,
      ],
      iepFormsk: [data.iepFormsk],
      groupTypeOther: [data.groupTypeOther],
      period: [data.period],
      ratio: [data.ratio],
      location: [data.location],
      locationOther: [data.locationOther],
      serviceStartDate: [
        data.serviceStartDate ? new Date(data.serviceStartDate) : null,
      ],
      serviceEndDate: [
        data.serviceEndDate ? new Date(data.serviceEndDate) : null,
      ],
      groupSize: [data.groupSize],
      serviceType: [data.serviceType],
      goalSeq: [data.goalSeq],
    });
  }

  addPlacementFirstAttendDateForm(): void {
    const control: FormArray = this.form.controls
      .placementFirstAttendDateForm as FormArray;
    this.placementsList?.forEach((item: PlacementAttendanceResponse) => {
      control.push(this.createPlacementAttendenceResponseForm(item));
    });
  }

  addIepProgramsFirstAttendDateForm(): void {
    const control: FormArray = this.form.controls
      .iepProgramsFirstAttendDateForm as FormArray;
    this.programsList?.forEach((item: Mandate) => {
      control.push(this.createIEPProgramForm(item));
    });
  }

  getPlacementFirstAttendDateControls(): AbstractControl[] {
    return (this.form.get('placementFirstAttendDateForm') as FormArray)
      .controls;
  }

  getIepProgramsFirstAttendDateControls(): AbstractControl[] {
    return (this.form.get('iepProgramsFirstAttendDateForm') as FormArray)
      .controls;
  }

  isSEITNotAlone(): boolean {
    return !(
      this.studentIEP?.programs?.length ===
      this.studentIEP?.programs.filter(
        (item: Mandate) => item.mandateType === this.seitString
      )?.length && this.studentIEP?.seitAgency
    );
  }

  submit(): void {
    this.loadSpinner = true;
    const formValue: PlacementFormValue[] = [
      ...this.form.get('placementFirstAttendDateForm').value,
      ...this.form.get('iepProgramsFirstAttendDateForm').value,
    ];
    const iepProgramsValue: PlacementFormValue = this.form.get(
      'iepProgramsFirstAttendDateForm'
    )?.value;
    const placementValue: PlacementFormValue[] = this.form.get(
      'placementFirstAttendDateForm'
    )?.value;
    const payload: FirstAttendDateRequest[] = formValue.map(
      (item: PlacementFormValue) => ({
        iepFormSK: iepProgramsValue?.[this.firstIndex]?.iepFormsk
          ? iepProgramsValue[this.firstIndex].iepFormsk
          : placementValue?.[this.firstIndex]?.iepFormSk,
        mandateSk: item.mandateSk,
        placementsk: item.placementSk,
        summerFlag: item.summerFlag,
        firstAttendDate: item.firstAttendDate,
        authorizationDate: item.authorizationDate,
      })
    );
    this.service
      .submitCaptureFirstAttendDate(payload)
      .subscribe((data: boolean) => {
        if (data) {
          this.loadSpinner = false;
          this.closeDialog.emit(false);
          this.saved.emit();
          this.notificationService.show({
            content: NOTIFICATION_MESSAGES.submitSuccess,
            cssClass: 'notification-success',
            hideAfter: dialogTimer,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: dialogAnimationDuration },
            type: { style: 'success', icon: true },
          });
        }
      });
  }

  get canCaptureFirstAttend(): boolean {
    return some(
      this.user?.permissions,
      (x: Permission) => x.permissionId === Permissions.CanCaptureFirstAttend
    );
  }
}
